import React, { useEffect, useState } from "react";
import { Card, Empty, ErrorState, Person, Pill, RowsSkeleton, Segmented, Table, Workspace } from "../ui";
import { Icon } from "../icons";
import { useFilters } from "../filters";
import { fetchSession } from "../data";
import { AGENT_LABELS, ENTITY_LABELS, PROBLEM_LABELS, RUN_STATUS, compact, fmt, ms, usd, when, whenShort, words } from "../format";
import { SourcePill } from "./Cost";
import Transcript from "./Transcript";

/**
 * One conversation, in a side panel: who, how it went, its runs and scores,
 * and the transcript as the user saw it. Deep-linked by `?session=`.
 */
export default function ConversationDrawer({ sessionId, onClose }) {
  const { f } = useFilters();
  const [state, setState] = useState({ data: null, error: null });
  const [tab, setTab] = useState("transcript");

  useEffect(() => {
    let live = true;
    setState({ data: null, error: null });
    fetchSession(sessionId, { environment: f.env, ...(f.ws ? { accountId: f.ws } : {}) })
      .then((data) => live && setState({ data, error: null }))
      .catch((err) => live && setState({ data: null, error: err?.response?.data?.error || err.message }));
    return () => {
      live = false;
    };
  }, [sessionId, f.env, f.ws]);

  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", key);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  const d = state.data;
  const byScorer = (id) => (d?.scores ?? []).filter((s) => s.scorerId === id);
  const friction = byScorer("friction").reduce((s, x) => s + (x.score || 0), 0);
  const problemCount = Object.values(d?.problems ?? {}).reduce((n, v) => n + (Number(v) || 0), 0);
  const created = (d?.entities ?? []).filter((e) => e.relation === "created");
  const health = byScorer("flow_health");
  const avgHealth = health.length ? health.reduce((s, x) => s + x.score, 0) / health.length : null;
  const failedRuns = d?.runs?.filter((r) => r.status === "failed").length ?? 0;

  return (
    <>
      <div className="aa-drawer-scrim" onClick={onClose} />
      <aside className="aa-drawer" role="dialog" aria-label="Conversation">
        <header style={{ padding: "18px 24px 0", background: "var(--aa-surface)", borderBottom: "1px solid var(--aa-border)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: "var(--aa-muted)", marginBottom: 4 }}>Conversation</div>
              <div style={{ fontSize: 16, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d?.conversation?.title?.replace(/\*\*/g, "") || d?.runs?.[0]?.input_text || (state.error ? "Could not load" : "Loading…")}
              </div>
              {d && (
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 10, flexWrap: "wrap", fontSize: 13 }}>
                  <Workspace id={d.conversation.account_id} />
                  <Person id={d.conversation.user_id} compact />
                  {d.conversation.created_at && <span style={{ color: "var(--aa-muted)" }}>{when(d.conversation.created_at)}</span>}
                </div>
              )}
            </div>
            {d?.traceUrl && (
              <a className="aa-btn aa-btn-sm" href={d.traceUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                Trace
                <Icon name="external" size={13} />
              </a>
            )}
            <button type="button" className="aa-btn aa-btn-sm aa-btn-icon aa-btn-ghost" onClick={onClose} aria-label="Close">
              <Icon name="x" size={16} />
            </button>
          </div>
          {d && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 1, margin: "16px -24px 0", background: "var(--aa-border)", borderTop: "1px solid var(--aa-border)" }}>
              <Stat label="Requests" value={fmt(d.runs.length)} sub={failedRuns ? `${failedRuns} failed` : "none failed"} tone={failedRuns ? "bad" : null} />
              <Stat label="Questions asked" value={fmt(d.interactions.length)} />
              <Stat label="Flow health" value={avgHealth == null ? "—" : avgHealth.toFixed(2)} tone={avgHealth == null ? null : avgHealth >= 0.95 ? "good" : avgHealth < 0.85 ? "bad" : "warn"} />
              <Stat label="Stuck moments" value={fmt(friction)} tone={friction ? "warn" : null} />
              <Stat label="AI cost" value={usd(d.cost?.totalUsd)} sub={d.calls?.length ? `${d.calls.length} model calls` : null} />
            </div>
          )}
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {state.error ? (
            <ErrorState error={state.error} />
          ) : !d ? (
            <RowsSkeleton rows={8} />
          ) : (
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <Segmented
                  value={tab}
                  onChange={setTab}
                  options={[
                    ["transcript", "Transcript"],
                    ["calls", `Model calls · ${d.calls?.length ?? 0}`],
                    ["runs", `Requests · ${d.runs.length}`],
                    ["scores", `Scores · ${d.scores.length}`],
                    ["problems", `Problems · ${problemCount}`],
                    ["created", `Created · ${created.length}`],
                  ]}
                />
              </div>
              {tab === "transcript" && <Transcript events={d.events} />}
              {tab === "problems" &&
                (problemCount ? (
                  <Card flush>
                    <Table
                      columns={[
                        { key: "code", label: "Problem", render: (r) => <span title={PROBLEM_LABELS[r.code]?.help}>{PROBLEM_LABELS[r.code]?.label ?? words(r.code)}</span> },
                        { key: "count", label: "Times", align: "right", render: (r) => fmt(r.count) },
                      ]}
                      rows={Object.entries(d.problems ?? {}).map(([code, count]) => ({ id: code, code, count }))}
                    />
                  </Card>
                ) : (
                  <Empty compact icon="checkCircle" title="No problems recorded in this conversation" />
                ))}
              {tab === "created" &&
                (created.length ? (
                  <Card flush>
                    <Table
                      columns={[
                        { key: "first_seen_at", label: "When", nowrap: true, render: (r) => whenShort(r.first_seen_at) },
                        { key: "entity_type", label: "What", render: (r) => <Pill tone="neutral">{ENTITY_LABELS[r.entity_type]?.one ?? words(r.entity_type)}</Pill> },
                        { key: "label", label: "Name", render: (r) => r.label || <span style={{ color: "var(--aa-faint)" }}>—</span> },
                        { key: "entity_id", label: "Id", render: (r) => <span className="aa-mono" style={{ fontSize: 12 }}>{r.entity_id}</span> },
                      ]}
                      rows={created.map((r, i) => ({ ...r, id: `${r.entity_type}-${r.entity_id}-${i}` }))}
                    />
                  </Card>
                ) : (
                  <Empty compact title="This conversation created nothing in HP" />
                ))}
              {tab === "calls" && <Calls calls={d.calls ?? []} />}
              {tab === "runs" && (
                <Card flush>
                  <Table
                    columns={[
                      { key: "seq", label: "#", align: "right", width: 40 },
                      { key: "input_text", label: "Request", max: 360, render: (r) => r.input_text || <span style={{ color: "var(--aa-faint)" }}>(picked an option)</span> },
                      {
                        key: "status",
                        label: "Status",
                        render: (r) => <Pill tone={RUN_STATUS[r.status]?.tone ?? "neutral"} dot>{RUN_STATUS[r.status]?.label ?? words(r.status)}</Pill>,
                      },
                      { key: "duration_ms", label: "Took", align: "right", render: (r) => ms(r.duration_ms) },
                      { key: "error", label: "Error", max: 260, render: (r) => (r.error ? <span style={{ color: "var(--aa-bad-ink)", fontSize: 12 }}>{r.error}</span> : "") },
                    ]}
                    rows={d.runs.map((r) => ({ ...r, id: r.id ?? r.seq }))}
                  />
                </Card>
              )}
              {tab === "scores" &&
                (d.scores.length ? (
                  <Card flush>
                    <Table
                      columns={[
                        { key: "timestamp", label: "When", nowrap: true, render: (r) => whenShort(r.timestamp) },
                        { key: "scorerId", label: "Scorer", render: (r) => <span className="aa-mono">{r.scorerId}</span> },
                        { key: "score", label: "Score", align: "right", render: (r) => fmt(r.score, 2) },
                        { key: "reason", label: "Reason", render: (r) => <span style={{ color: "var(--aa-ink-2)" }}>{r.reason}</span> },
                      ]}
                      rows={d.scores.map((s, i) => ({ ...s, id: i }))}
                    />
                  </Card>
                ) : (
                  <Empty compact icon="quality" title="No scores recorded for this conversation" />
                ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

const TONE = { good: "var(--aa-good-ink)", bad: "var(--aa-bad-ink)", warn: "var(--aa-warn-ink)" };

function Stat({ label, value, sub, tone }) {
  return (
    <div style={{ background: "var(--aa-surface)", padding: "12px 16px" }}>
      <div style={{ fontSize: 12, color: "var(--aa-muted)" }}>{label}</div>
      <div className="aa-num" style={{ fontSize: 18, fontWeight: 600, marginTop: 2, color: TONE[tone] ?? "var(--aa-ink)" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--aa-muted)" }}>{sub}</div>}
    </div>
  );
}

/**
 * Every model call in the conversation, turn by turn: what it was for, where
 * it ran (agent, tool, step), its model, tokens, time and cost.
 */
function Calls({ calls }) {
  if (!calls.length) return <Empty compact icon="dollar" title="No model calls recorded for this conversation">Tracing must be on where the agent runs.</Empty>;
  const turns = [];
  for (const c of calls) {
    const key = c.turn?.spanId ?? "none";
    let t = turns.find((x) => x.key === key);
    if (!t) turns.push((t = { key, label: c.turn?.label ?? "Outside a turn", calls: [] }));
    t.calls.push(c);
  }
  const sum = (cs, f) => cs.reduce((s, c) => s + (f(c) ?? 0), 0);
  const priced = calls.filter((c) => c.usd != null);
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="aa-card" style={{ display: "flex", gap: 24, flexWrap: "wrap", padding: "12px 16px", fontSize: 13 }}>
        <span>
          <b className="aa-num">{fmt(calls.length)}</b> calls
        </span>
        <span>
          <b className="aa-num">{compact(sum(calls, (c) => c.tokens.input))}</b> tokens in · <b className="aa-num">{compact(sum(calls, (c) => c.tokens.output))}</b> out
        </span>
        <span>
          <b className="aa-num">{priced.length ? usd(sum(priced, (c) => c.usd)) : "—"}</b>
          {priced.length < calls.length && <span style={{ color: "var(--aa-muted)" }}> · {calls.length - priced.length} unpriced</span>}
        </span>
      </div>
      {turns.map((t) => (
        <Card key={t.key} flush title={t.label} subtitle={`${t.calls.length} call${t.calls.length === 1 ? "" : "s"} · ${t.calls.some((c) => c.usd != null) ? usd(sum(t.calls, (c) => c.usd)) : "unpriced"}`}>
          <Table
            columns={[
              { key: "startedAt", label: "When", nowrap: true, render: (r) => (r.startedAt ? new Date(r.startedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—") },
              {
                key: "call",
                label: "Call",
                sort: (r) => r.purpose ?? r.agent,
                render: (r) => (
                  <span>
                    <span className="aa-mono" style={{ fontWeight: 600, fontSize: 12.5, color: r.error ? "var(--aa-bad-ink)" : undefined }}>
                      {r.purpose ?? r.agent ?? r.name}
                    </span>
                    <span className="aa-sub">
                      {[r.purpose && r.agent ? `in ${r.agent}` : !r.purpose && r.agent ? AGENT_LABELS[r.agent] ?? "agent run" : null, r.tool ? `tool ${r.tool}` : null, r.step ? `step ${r.step}` : null, r.steps > 1 ? `${r.steps} round-trips` : null]
                        .filter(Boolean)
                        .join(" · ") || "outside an agent"}
                    </span>
                  </span>
                ),
              },
              { key: "model", label: "Model", render: (r) => <span className="aa-mono" style={{ color: "var(--aa-ink-2)" }}>{r.model ?? "—"}</span> },
              {
                key: "in",
                label: "In",
                align: "right",
                sort: (r) => r.tokens.input,
                render: (r) => (
                  <span title={`${fmt(r.tokens.cacheRead)} cached read · ${fmt(r.tokens.cacheWrite)} cache write`}>
                    {fmt(r.tokens.input)}
                    {r.tokens.cacheRead > 0 && <span className="aa-sub">{Math.round((r.tokens.cacheRead / Math.max(1, r.tokens.input)) * 100)}% cached</span>}
                  </span>
                ),
              },
              {
                key: "out",
                label: "Out",
                align: "right",
                sort: (r) => r.tokens.output,
                render: (r) => (
                  <span>
                    {fmt(r.tokens.output)}
                    {r.tokens.reasoning > 0 && <span className="aa-sub">{fmt(r.tokens.reasoning)} reasoning</span>}
                  </span>
                ),
              },
              { key: "durationMs", label: "Time", align: "right", render: (r) => ms(r.durationMs) },
              {
                key: "usd",
                label: "Cost",
                align: "right",
                render: (r) => (
                  <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                    <span style={{ fontWeight: 600 }}>{r.usd == null ? "—" : usd(r.usd)}</span>
                    <SourcePill source={r.source} />
                  </span>
                ),
              },
            ]}
            rows={t.calls.map((c) => ({ ...c, id: c.spanId }))}
          />
        </Card>
      ))}
    </div>
  );
}
